import { createAction, props } from '@ngrx/store';

import { RealtimeConnectionState } from '@app/core/realtime/realtime.service';
import {
  ConnectedDevice,
  DeviceDisconnectedPayload,
  DisconnectReason,
  IdentifiedPayload,
  IdentifyFailureReason,
  WsEnvelope,
} from '@app/core/realtime/realtime.types';

/** Trigger lifecycle: open socket and IDENTIFY using the current access token. */
export const realtimeConnect = createAction('[Realtime] Connect');

/** User-initiated teardown (logout, language switch, ngOnDestroy). */
export const realtimeDisconnect = createAction('[Realtime] Disconnect');

/** Connection state transitions surfaced by RealtimeService. */
export const realtimeConnectionStateChanged = createAction(
  '[Realtime] Connection State Changed',
  props<{ state: RealtimeConnectionState }>()
);

/** Server confirmed the IDENTIFY handshake. */
export const realtimeConnected = createAction(
  '[Realtime] Connected',
  props<{
    socketInfo: IdentifiedPayload;
    envelope: WsEnvelope<IdentifiedPayload>;
  }>()
);

/** Server replied IDENTIFY_FAILED. The reason decides what happens next. */
export const realtimeIdentifyFailed = createAction(
  '[Realtime] Identify Failed',
  props<{
    reason: IdentifyFailureReason;
    envelope: WsEnvelope<{ reason: IdentifyFailureReason }>;
  }>()
);

/** Server sent the initial device roster immediately after IDENTIFY. */
export const realtimeDeviceListReceived = createAction(
  '[Realtime] Device List Received',
  props<{ devices: ConnectedDevice[] }>()
);

/** A peer device joined the account room. */
export const realtimeDeviceConnected = createAction(
  '[Realtime] Device Connected',
  props<{ device: ConnectedDevice }>()
);

/** A peer device left the account room. */
export const realtimeDeviceDisconnected = createAction(
  '[Realtime] Device Disconnected',
  props<{ event: DeviceDisconnectedPayload }>()
);

/**
 * The server forcibly closed our socket for a security reason. Triggers
 * `forceLogout`.
 */
export const realtimeForceDisconnected = createAction(
  '[Realtime] Force Disconnected',
  props<{ reason: DisconnectReason }>()
);

/**
 * Catch-all for Kafka-forwarded business events. The dispatcher effect maps
 * each envelope type to feature actions.
 */
export const realtimeEventReceived = createAction(
  '[Realtime] Event Received',
  props<{ envelope: WsEnvelope }>()
);
